import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { GridFilterModel } from "@mui/x-data-grid";
import { transformFilterItemToFilterObject } from "../utils/helper";

interface FiltersState {
	filters: {
		[key: string]: GridFilterModel | undefined;
	};
	/**
	 * Sets or updates a filter model for a specific key.
	 *
	 * @param key - The key representing the filter's context (e.g., table name or page).
	 * @param filterModal - The new filter model to set, or undefined to clear.
	 */
	setFilters: (key: string, filterModal?: GridFilterModel) => void;

	/**
	 * Clears the filter model for a specific key.
	 *
	 * @param key - The key representing the filter's context.
	 */
	clearFilters: (key: string) => void;

	/**
	 * Retrieves the first filter field-value pair from a filter model for a specific key.
	 *
	 * @param key - The key representing the filter's context.
	 * @returns An object containing `field` and `value`, or `undefined` if no filter exists.
	 */
	getFilterObject: (key: string) => { field: string; value: any } | undefined;
}

/**
 * Zustand store for managing DataGrid filter states.
 * 
 * - Persists data to `sessionStorage` under the key "filters".
 * - Supports setting, clearing, and retrieving filters by key.
 * 
 */
const useFiltersStore = create<FiltersState>()(
	persist(
		(set, get) => ({
			filters: {},
			setFilters: (key, filterModal) =>
				set(() => ({
					filters: { ...get().filters, [key]: filterModal },
				})),
			clearFilters: (key) =>
				set(() => {
					const filters = get().filters;
					delete filters[key];
					return {
						filters: filters,
					};
				}),
			getFilterObject: (key) => {
				const filters = get().filters[key];
				return filters && transformFilterItemToFilterObject(filters.items[0]);
			}
		}),
		{
			name: "filters",
			storage: createJSONStorage(() => sessionStorage),
		}
	)
);

export default useFiltersStore;
